import React, { useEffect, useState } from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { Activity, ExternalLink, RefreshCw } from "lucide-react";

import { Banner } from "@/design-system/components/Banner/Banner";
import { Button } from "@/design-system/components/Button/Button";
import { Input } from "@/design-system/components/Input/Input";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import useCurrentEnvironment from "@/hooks/use-environment";
import {
  LiveViewEvent,
  useLiveEventStream,
} from "@/LiveViewPage/liveViewConnection";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import {
  eventLabel,
  firstEventAfterSiteChange,
  firstEventForSite,
  isSiteInstallEvent,
  toFirstEvent,
} from "../installEvent";
import { toSiteUrl } from "../siteUrl";
import { InstallSdkStepProps } from "../types";
import { useRecordEventsSeen } from "../eventsSeen";

const MAX_EVENTS = 10;
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;

type ReceivedEvent = LiveViewEvent & { key: string; receivedAt: number };

export function VerifyEventsStep({
  data,
  updateData,
  goNext,
}: InstallSdkStepProps) {
  const envID = useCurrentEnvironment()?.ID;
  useRecordEventsSeen(envID, data.firstEvent !== null);
  const siteUrl = toSiteUrl(data.siteUrl);
  const firstEvent = firstEventForSite(data.firstEvent, siteUrl);
  const [events, setEvents] = useState<ReceivedEvent[]>([]);
  const [ignoredCount, setIgnoredCount] = useState(0);
  const [listening, setListening] = useState(firstEvent === null);
  const [matchedSinceListening, setMatchedSinceListening] = useState(0);
  const [lastActivityAt, setLastActivityAt] = useState(() => Date.now());
  const siteEvents = events.filter((event) =>
    isSiteInstallEvent(event, siteUrl),
  );
  const seen = firstEvent !== null;

  useLiveEventStream(
    envID,
    (event) => {
      if (!isSiteInstallEvent(event, siteUrl)) {
        setIgnoredCount((count) => count + 1);
        return;
      }
      if (firstEvent === null) {
        updateData((latest) =>
          firstEventForSite(latest.firstEvent, siteUrl) === null
            ? { firstEvent: toFirstEvent(event) }
            : {},
        );
      }
      setEvents((current) =>
        [
          {
            ...event,
            key: `${Date.now()}-${Math.random()}`,
            receivedAt: Date.now(),
          },
          ...current,
        ].slice(0, MAX_EVENTS),
      );
      setMatchedSinceListening((count) => count + 1);
      setLastActivityAt(Date.now());
    },
    listening,
  );

  useEffect(() => {
    if (matchedSinceListening >= MAX_EVENTS) {
      setListening(false);
    }
  }, [matchedSinceListening]);

  useEffect(() => {
    if (!listening) {
      return;
    }
    const timeout = window.setTimeout(
      () => setListening(false),
      Math.max(0, lastActivityAt + IDLE_TIMEOUT_MS - Date.now()),
    );
    return () => window.clearTimeout(timeout);
  }, [listening, lastActivityAt]);

  function keepListening() {
    setMatchedSinceListening(0);
    setLastActivityAt(Date.now());
    setListening(true);
  }

  return (
    <WizardStepLayout
      title="Check that events are arriving"
      description="Open your site in another tab and click around. Each page view and click from your site shows up here, and in Live View, as soon as Freshpaint receives it."
      primaryAction={{
        label: seen ? "Finish" : "Finish without checking",
        onClick: goNext,
      }}
    >
      <div className="flex items-end gap-2">
        <Input
          className="flex-1"
          label="Your site’s address"
          placeholder="www.example.com"
          value={data.siteUrl}
          onChange={(event) => {
            const nextSiteUrl = event.target.value;
            updateData((latest) => ({
              siteUrl: nextSiteUrl,
              firstEvent: firstEventAfterSiteChange(
                latest.firstEvent,
                events,
                toSiteUrl(nextSiteUrl),
              ),
            }));
            keepListening();
          }}
        />
        <Button
          variant="outline"
          leadingIcon={<ExternalLink aria-hidden />}
          disabled={!siteUrl}
          tooltipContent={siteUrl ? undefined : "Enter your site’s address"}
          onClick={() => {
            if (siteUrl) {
              window.open(siteUrl, "_blank", "noopener,noreferrer");
            }
          }}
        >
          Open site
        </Button>
      </div>
      <div className="flex items-center justify-between gap-4">
        {seen ? (
          <Banner type="success" className="flex-1">
            Freshpaint is receiving events from your site.
          </Banner>
        ) : listening ? (
          <div className="flex items-center gap-3">
            <LoadingSpinner className="size-5" />
            <Typography variant="body-sm" color="subtle">
              Waiting for the first event…
            </Typography>
          </div>
        ) : (
          <Typography variant="body-sm" color="subtle">
            Stopped listening after 10 minutes without an event from your site.
          </Typography>
        )}
        <div className="flex shrink-0 items-center gap-3">
          {!listening && (
            <Button
              variant="outline"
              size="sm"
              leadingIcon={<RefreshCw aria-hidden />}
              onClick={keepListening}
            >
              Keep listening
            </Button>
          )}
          <WizardLink
            to="/events/liveview"
            className="text-label-sm text-textActionSelected flex cursor-pointer items-center gap-1 underline"
          >
            <Activity className="size-icon-sm" aria-hidden />
            Watch in Live View
          </WizardLink>
        </div>
      </div>
      {siteEvents.length > 0 && (
        <ul className="border-borderSubtle divide-borderSubtle divide-y rounded-lg border">
          {siteEvents.map((event) => (
            <li
              key={event.key}
              className="flex items-center justify-between gap-4 px-4 py-2"
            >
              <span className="text-label-sm text-textPrimary truncate">
                {eventLabel(event)}
              </span>
              <span className="text-body-xs text-textTertiary truncate">
                {event.built_in_properties?.host}
                {event.path}
              </span>
              <span className="text-body-xs text-textTertiary shrink-0">
                {formatDistanceToNowStrict(event.receivedAt, {
                  addSuffix: true,
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
      {ignoredCount > 0 && (
        <Typography variant="body-sm" color="subtle">
          {ignoredCount === 1 ? "1 event" : `${ignoredCount} events`} didn’t
          count because {ignoredCount === 1 ? "it" : "they"} came from{" "}
          {siteUrl
            ? `a server or mobile SDK, an integration, or a site other than ${new URL(siteUrl).host}.`
            : "a server or mobile SDK, or an integration, instead of the Freshpaint snippet."}
        </Typography>
      )}
      {siteUrl && !seen && siteEvents.length === 0 && (
        <Typography variant="body-sm" color="subtle">
          Nothing yet? Make sure the change is published, then reload your site.
          {data.method === "gtm" &&
            " In Google Tag Manager, Preview mode only fires tags in the preview window, so submit and publish the container before checking your live site."}{" "}
          Ad blockers and consent banners can also hold events back. Live View
          shows every event Freshpaint receives, so you can come back to check
          there anytime.
        </Typography>
      )}
    </WizardStepLayout>
  );
}
